import React, { useState } from 'react';
import { Bell, Download } from 'lucide-react';
import {
  generateGoogleCalendarUrl,
  generateYahooCalendarUrl,
  generateOutlookUrl,
  downloadICSFile
} from '../utils/calendar';

const REMINDER_OPTIONS = [
  { days: 7, label: '7 Days' },
  { days: 5, label: '5 Days' },
  { days: 1, label: '1 Day' },
];

export default function SetUpReminder({ debit }) {
  const [selectedDays, setSelectedDays] = useState([7, 5, 1]);

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day].sort((a, b) => b - a)
    );
  };

  if (!debit) return null;

  return (
    <div className="mt-6 card-default p-6 border-2 border-primary/30 bg-primary/5" data-testid="setup-reminder-section">
      <div className="flex items-center gap-3 mb-1">
        <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center">
          <Bell className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-dark text-lg">SET UP REMINDER</h2>
          <p className="text-sm text-text-secondary">Add to your calendar so you never miss this payment</p>
        </div>
      </div>

      {/* Reminder Days Selector */}
      <div className="mt-4 mb-4" data-testid="reminder-days-selector">
        <p className="text-sm font-medium text-text-primary mb-2">Remind me before:</p>
        <div className="flex gap-2">
          {REMINDER_OPTIONS.map(({ days, label }) => (
            <button
              key={days}
              type="button"
              onClick={() => toggleDay(days)}
              data-testid={`reminder-day-${days}`}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                selectedDays.includes(days)
                  ? 'bg-primary text-white shadow-sm'
                  : 'bg-white text-text-secondary border border-border hover:border-primary'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-2">
        <button
          type="button"
          onClick={() => { window.open(generateGoogleCalendarUrl(debit, selectedDays), '_blank'); }}
          className="flex items-center gap-3 w-full px-4 py-3 bg-white rounded-xl border border-border hover:border-primary hover:shadow-sm transition-all text-left"
          data-testid="reminder-google"
        >
          <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="none">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <div>
            <span className="font-medium text-dark text-sm">Google Calendar</span>
            <span className="block text-xs text-text-light">Android, Gmail, Web</span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { window.open(generateOutlookUrl(debit, selectedDays), '_blank'); }}
          className="flex items-center gap-3 w-full px-4 py-3 bg-white rounded-xl border border-border hover:border-primary hover:shadow-sm transition-all text-left"
          data-testid="reminder-outlook"
        >
          <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="#0072C6">
            <path d="M24 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 01-.586.234h-8.176v-6.09l1.406 1.064a.313.313 0 00.196.066.333.333 0 00.212-.076l.004-.002a.322.322 0 00.108-.133.348.348 0 00.036-.159v-.014a.348.348 0 00-.036-.159.322.322 0 00-.108-.133l-1.818-1.375V7.387h8.176c.23 0 .424.078.586.234a.782.782 0 01.238.576z"/>
            <path d="M9.266 7.387v10.478c0 .23-.08.424-.238.576a.806.806 0 01-.586.234H.824a.806.806 0 01-.586-.234A.782.782 0 010 17.865V7.387c0-.23.08-.424.238-.576a.806.806 0 01.586-.234h7.618c.23 0 .424.078.586.234a.782.782 0 01.238.576z"/>
            <ellipse cx="4.633" cy="12.627" rx="2.633" ry="2.73" fill="white"/>
          </svg>
          <div>
            <span className="font-medium text-dark text-sm">Outlook</span>
            <span className="block text-xs text-text-light">Microsoft, Hotmail</span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { window.open(generateYahooCalendarUrl(debit, selectedDays), '_blank'); }}
          className="flex items-center gap-3 w-full px-4 py-3 bg-white rounded-xl border border-border hover:border-primary hover:shadow-sm transition-all text-left"
          data-testid="reminder-yahoo"
        >
          <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="#5F01D1">
            <path d="M13.131 10.948l4.354-9.534h3.066l-5.89 12.9v7.686h-2.903v-7.635l-5.94-12.95h3.187z"/>
          </svg>
          <div>
            <span className="font-medium text-dark text-sm">Yahoo Calendar</span>
            <span className="block text-xs text-text-light">Yahoo Mail</span>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { downloadICSFile(debit, selectedDays); }}
          className="flex items-center gap-3 w-full px-4 py-3 bg-white rounded-xl border border-border hover:border-primary hover:shadow-sm transition-all text-left"
          data-testid="reminder-ics"
        >
          <Download className="w-6 h-6 text-text-secondary flex-shrink-0" />
          <div>
            <span className="font-medium text-dark text-sm">Download .ics file</span>
            <span className="block text-xs text-text-light">Apple Calendar, Samsung, Other</span>
          </div>
        </button>
      </div>
    </div>
  );
}
